import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  type Ref,
  ref,
  watch,
} from 'vue';
import {
  type SwipeDirection,
  useHorizontalSwipe,
} from '../../../shared/composables/use-horizontal-swipe';

interface ImageGridPagerOptions {
  edgeTapZone: number;
  getItemCount: () => number;
  getColumnCount: () => number;
  getRowCount: () => number;
  getPageCount: () => number;
  onViewportChange: (width: number, height: number) => void;
  onVisibleItemsChange: (uuids: string[]) => void;
}

interface ImageGridPager {
  canScroll: () => boolean;
  getImageEl: (index: number) => HTMLImageElement | null;
  hideNextArrow: Ref<boolean>;
  hidePrevArrow: Ref<boolean>;
  isItemVisible: (uuid: string) => boolean;
  onPointerDown: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  root: Ref<HTMLElement | null>;
  scheduleScrollStateUpdate: (emitVisibleItemsChange?: boolean) => void;
  scrollable: Ref<HTMLElement | null>;
  scrollByPage: (direction: 1 | -1) => void;
  scrollToItem: (uuid: string) => void;
}

export function useImageGridPager(
  options: ImageGridPagerOptions
): ImageGridPager {
  const root = ref<HTMLElement | null>(null);
  const scrollable = ref<HTMLElement | null>(null);
  const hidePrevArrow = ref(true);
  const hideNextArrow = ref(true);
  let resizeObserver: ResizeObserver | null = null;
  let scrollRaf = 0;
  let visibleItemUuids: string[] = [];

  function updateViewport(): void {
    const element = root.value;
    if (!element) {
      return;
    }
    options.onViewportChange(element.clientWidth, element.clientHeight);
    scheduleScrollStateUpdate(false);
  }

  function updateScrollState(emitVisibleItemsChange = false): void {
    const element = scrollable.value;
    if (!element) {
      hidePrevArrow.value = true;
      hideNextArrow.value = true;
      return;
    }
    const pageCount = options.getPageCount();
    const hasPagesToScroll =
      pageCount > 1 && element.scrollWidth > element.clientWidth + 2;
    const pageIndex = currentPageIndex(element);
    hidePrevArrow.value = !hasPagesToScroll || pageIndex <= 0;
    hideNextArrow.value = !hasPagesToScroll || pageIndex >= pageCount - 1;

    const uuids = visibleUuids();
    if (uuids.join('|') !== visibleItemUuids.join('|')) {
      visibleItemUuids = uuids;
      if (emitVisibleItemsChange) {
        options.onVisibleItemsChange(uuids);
      }
    }
  }

  function scheduleScrollStateUpdate(emitVisibleItemsChange = false): void {
    if (scrollRaf) {
      cancelAnimationFrame(scrollRaf);
    }
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      updateScrollState(emitVisibleItemsChange);
    });
  }

  function pageElements(): HTMLElement[] {
    return Array.from(
      scrollable.value?.querySelectorAll<HTMLElement>('.ImageGrid__page') ?? []
    );
  }

  function pageScrollLeft(
    page: HTMLElement,
    pageList = pageElements()
  ): number {
    const firstPageLeft = pageList[0]?.offsetLeft ?? 0;
    return Math.max(0, page.offsetLeft - firstPageLeft);
  }

  function currentPageIndex(element = scrollable.value): number {
    if (!element) {
      return 0;
    }
    const pageList = pageElements();
    if (pageList.length <= 1) {
      return 0;
    }
    const scrollLeft = Math.max(0, element.scrollLeft);
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    pageList.forEach((page, index) => {
      const distance = Math.abs(pageScrollLeft(page, pageList) - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  function pageIndexForItem(uuid: string): number {
    const item = itemElement(uuid);
    const page = item?.closest('.ImageGrid__page') as HTMLElement | null;
    if (!page) {
      return -1;
    }
    return pageElements().indexOf(page);
  }

  function scrollToPageIndex(
    index: number,
    behavior: ScrollBehavior = 'smooth'
  ): void {
    const element = scrollable.value;
    const pageList = pageElements();
    if (!(element && pageList.length > 0)) {
      return;
    }
    const targetIndex = Math.min(pageList.length - 1, Math.max(0, index));
    const targetPage = pageList[targetIndex];
    if (!targetPage) {
      return;
    }
    const targetLeft = Math.min(
      Math.max(0, element.scrollWidth - element.clientWidth),
      pageScrollLeft(targetPage, pageList)
    );
    if (Math.abs(targetLeft - element.scrollLeft) <= 1) {
      updateScrollState(false);
      return;
    }
    element.scrollTo({ left: targetLeft, behavior });
    scheduleScrollStateUpdate(false);
  }

  function visibleUuids(): string[] {
    const element = scrollable.value;
    if (!element) {
      return [];
    }
    const currentPage = pageElements()[currentPageIndex(element)];
    if (currentPage) {
      return Array.from(
        currentPage.querySelectorAll<HTMLElement>('[data-uuid]')
      )
        .map((item) => item.dataset.uuid)
        .filter((uuid): uuid is string => Boolean(uuid));
    }

    const viewport = element.getBoundingClientRect();
    return Array.from(element.querySelectorAll<HTMLElement>('[data-uuid]'))
      .filter((item) => {
        const rect = item.getBoundingClientRect();
        return rect.right > viewport.left + 1 && rect.left < viewport.right - 1;
      })
      .map((item) => item.dataset.uuid)
      .filter((uuid): uuid is string => Boolean(uuid));
  }

  function itemElement(uuid: string): HTMLElement | null {
    return (
      Array.from(
        scrollable.value?.querySelectorAll<HTMLElement>('[data-uuid]') ?? []
      ).find((element) => element.dataset.uuid === uuid) ?? null
    );
  }

  function scrollByPage(direction: 1 | -1): void {
    const element = scrollable.value;
    if (!element) {
      return;
    }
    const pageList = pageElements();
    if (pageList.length <= 1) {
      updateScrollState(false);
      return;
    }
    const targetIndex = Math.min(
      pageList.length - 1,
      Math.max(0, currentPageIndex(element) + direction)
    );
    if (targetIndex === currentPageIndex(element)) {
      updateScrollState(false);
      return;
    }
    scrollToPageIndex(targetIndex);
  }

  function onEdgeTap(direction: SwipeDirection): void {
    scrollByPage(direction === 'next' ? 1 : -1);
  }

  function canScroll(): boolean {
    const element = scrollable.value;
    return Boolean(
      element &&
        options.getPageCount() > 1 &&
        element.scrollWidth > element.clientWidth + 2
    );
  }

  function isItemVisible(uuid: string): boolean {
    const pageIndex = pageIndexForItem(uuid);
    if (pageIndex < 0) {
      return false;
    }
    return pageIndex === currentPageIndex();
  }

  function scrollToItem(uuid: string): void {
    const pageIndex = pageIndexForItem(uuid);
    if (pageIndex >= 0) {
      scrollToPageIndex(pageIndex);
    }
  }

  function getImageEl(index: number): HTMLImageElement | null {
    return (
      scrollable.value?.querySelectorAll<HTMLImageElement>(
        '.ImageGrid__item-image'
      )[index] ?? null
    );
  }

  const { onPointerDown, onPointerUp } = useHorizontalSwipe({
    edgeTapZone: options.edgeTapZone,
    onEdgeTap,
  });

  watch(
    () => [
      options.getItemCount(),
      options.getColumnCount(),
      options.getRowCount(),
    ],
    () => {
      nextTick(() => {
        updateViewport();
        updateScrollState(false);
      });
    }
  );

  onMounted(() => {
    updateViewport();
    resizeObserver = new ResizeObserver(updateViewport);
    if (root.value) {
      resizeObserver.observe(root.value);
    }
    nextTick(() => updateScrollState(false));
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    if (scrollRaf) {
      cancelAnimationFrame(scrollRaf);
    }
  });

  return {
    canScroll,
    getImageEl,
    hideNextArrow,
    hidePrevArrow,
    isItemVisible,
    onPointerDown,
    onPointerUp,
    root,
    scheduleScrollStateUpdate,
    scrollable,
    scrollByPage,
    scrollToItem,
  };
}
